/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 공통 컨트롤러 클래스를 정의한다.
 *
 * @file /src/elements/control/Control.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 25.
 */
import * as parent from '@base/Element';
import { Language, LanguageText } from '@utils/Language';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import { Dragger } from '@utils/Dragger';
import { Scroller } from '@utils/Scroller';

export abstract class Control extends parent.Element {
    /**
     * 엘리먼트 타입
     */
    type: string = 'control';

    /**
     * 아이콘
     */
    icon: string = null;

    /**
     * 제목
     */
    text: LanguageText = null;

    /**
     * 클릭시 활성화여부 (false 인 경우 포인터다운시 활성화한다.)
     */
    activeOnClick: boolean = false;

    /**
     * 이동가능여부
     */
    movable: boolean = false;

    /**
     * 닫기가능여부
     */
    closable: boolean = false;

    /**
     * 버튼 DOM
     */
    $button: Dom = null;

    /**
     * 아이콘 DOM
     */
    $icon: Dom = null;

    /**
     * 제목 DOM
     */
    $text: Dom = null;

    /**
     * 드래그 앤 드롭 상태
     *
     * @property scroller - 브라우저내에서 스크롤영역의 절대위치
     * @property target - 스크롤영역내에서의 드래그대상의 현재 위치 (스크롤 또는 DOM 이동시 초기화됨)
     * @property offset - 마우스포인터와 드래그대상 좌측끝 사이의 초기 오프셋
     * @property tracker - 드래거
     * @property $preview - 드래그 중 표시되는 프리뷰 DOM
     */
    drag: {
        scroller: DOMRect;
        target: number;
        offset: number;
        tracker: Dragger;
        onScroll: () => void;
        $preview: Dom;
    } = {
        scroller: null,
        target: null,
        offset: null,
        tracker: null,
        onScroll: null,
        $preview: null,
    };

    /**
     * 엘리먼트 DOM 을 가져온다.
     *
     * @returns 엘리먼트 DOM
     */
    $getElement(): Dom {
        if (this.$element === null) {
            this.$element = Html.create('li');
            this.$element.setData('closable', this.closable);
            this.$element.setData('movable', this.movable);
        }

        return this.$element;
    }

    /**
     * 버튼 DOM 을 가져온다.
     *
     * @returns 버튼 DOM
     */
    $getButton(): Dom {
        if (this.$button === null) {
            this.$button = Html.create('button', {
                type: 'button',
            });

            this.$button.append(this.$getIcon());
            this.$button.append(this.$getText());

            this.$button.on('pointerdown', async (_, e) => {
                if (this.$getElement().hasClass('loading') == true || e.button !== 0) {
                    return;
                }

                if (this.activeOnClick == false) {
                    await this.active();
                }
            });

            this.$button.on('click', async (_, e) => {
                if (this.activeOnClick == true) {
                    if (this.$getElement().hasClass('loading') == true || e.button !== 0) {
                        return;
                    }

                    await this.active();
                }
            });

            if (this.movable == true) {
                this.#initDragAndDrop();
            }
        }

        return this.$button;
    }

    /**
     * 버튼아이콘 DOM 을 가져온다.
     *
     * @returns 버튼아이콘 DOM
     */
    $getIcon(): Dom {
        if (this.$icon === null) {
            this.$icon = Html.icon(this.icon ?? '');
        }

        return this.$icon;
    }

    /**
     * 아이콘을 변경한다.
     *
     * @param icon - 변경할 아이콘 (NULL 인 경우 아이콘을 제거한다.)
     */
    setIcon(icon: string | null = null): void {
        const $icon = this.$getIcon();
        $icon.removeAttr('class');
        $icon.removeAttr('style');

        if (icon !== null) {
            $icon.addClass(...icon.split(' '));
        }
    }

    /**
     * 아이콘을 가져온다.
     *
     * @returns 아이콘
     */
    getIcon(): string {
        return this.icon;
    }

    /**
     * 제목 DOM 을 가져온다.
     *
     * @returns 제목 DOM
     */
    $getText(): Dom {
        if (this.$text === null) {
            this.$text = Html.create('span', null, 'Loading...');
        }

        return this.$text;
    }

    /**
     * 텍스트를 변경한다.
     *
     * @param text - 변경할 텍스트
     * @param is_replace - 기존 텍스트를 대치할지 여부 (false 인 경우 화면표시만 변경한다.)
     */
    async setText(text: LanguageText, is_replace: boolean = false): Promise<void> {
        const $text = this.$getText();

        if (is_replace === true) {
            this.text = text;
        }

        $text.html(await Language.resolveText(text));
        this.onUpdate();
    }

    /**
     * 텍스트를 가져온다.
     *
     * @returns 텍스트
     */
    async getText(): Promise<string> {
        return await Language.resolveText(this.text);
    }

    /**
     * 컨테이너 DOM을 가져온다 (드래그 앤 드롭용)
     *
     * @returns 컨테이너 DOM
     */
    abstract getContainer(): Dom;

    /**
     * 스크롤러를 가져온다 (드래그 앤 드롭용)
     *
     * @returns 스크롤러
     */
    abstract getScroller(): Scroller;

    /**
     * 컨트롤을 활성화한다.
     */
    abstract active(): Promise<void>;

    /**
     * 컨트롤이 위치한 곳으로 스크롤한다.
     */
    scrollTo(): void {
        const $element = this.$getElement();
        const $scrollable = this.getScroller().$target;

        if ($scrollable !== null) {
            const { left } = $element.getPosition($scrollable);

            let padding = 0;
            if ($element.prev() === null || $element.next() === null) {
                padding = (parseInt(this.getContainer().getStyle('margin-left').replace('px', ''), 10) || 0) + 1;
            } else {
                padding = (parseInt(this.getContainer().getStyle('gap').replace('px', ''), 10) || 0) + 1;
            }

            if (left < 0 || left + $element.getOuterWidth() > $scrollable.getOuterWidth()) {
                $element.scrollTo(true, $scrollable, { left: padding, right: padding });
            }
        }
    }

    /**
     * 활성화 상태를 설정한다.
     *
     * @param active - 활성화여부
     */
    setActive(active: boolean): void {
        if (active == true) {
            this.$getButton().addClass('active');
            this.scrollTo();
        } else {
            this.$getButton().removeClass('active');
        }
    }

    /**
     * 활성화 상태인지 확인한다.
     *
     * @returns 활성화여부
     */
    isActive(): boolean {
        return this.$getButton().hasClass('active');
    }

    /**
     * 로딩여부를 설정한다.
     *
     * @param loading - 로딩여부
     */
    setLoading(loading: boolean): void {
        if (loading == true) {
            this.$getButton().addClass('loading');
        } else {
            this.$getButton().removeClass('loading');
        }
    }

    /**
     * 엘리먼트를 렌더링한다.
     */
    async render(): Promise<void> {
        if (this.isRendered() == true) {
            return;
        }

        Language.resolveText(this.text).then((text) => {
            this.$getText().html(text);
        });

        this.$getElement().append(this.$getButton());

        await this.onRender();
    }

    /**
     * 엘리먼트를 제거한다.
     *
     * @param removeDom - 엘리먼트 DOM 을 제거할지 여부
     */
    async remove(removeDom: boolean = true): Promise<void> {
        this.$button = null;
        this.$icon = null;
        this.$text = null;

        super.remove(removeDom);
    }

    /**
     * 컨트롤이 변경되었을 때 이벤트를 처리한다.
     */
    abstract onUpdate(): Promise<void>;

    /**
     * 드래그 앤 드롭 기능을 초기화한다.
     */
    #initDragAndDrop(): void {
        if (this.$element === null) {
            return;
        }

        if (this.$element.getData('drag') === null) {
            const drag = new Dragger(this.$getElement(), {
                cursor: 'ew-resize',
                delay: 500,
                listeners: {
                    start: (_, tracker) => this.#handleDragStart(tracker),
                    drag: () => this.#handleDrag(),
                    end: () => this.#handleDragEnd(),
                },
            });

            this.$element.setData('drag', drag, false);
        }
    }

    /**
     * 스크롤영역내에서의 드래그대상의 현재 위치를 가져온다.
     * 위치가 변경되지 않은 경우 캐싱된 값을 재사용한다.
     *
     * @returns 드래그대상의 현재 위치
     */
    async #getDragTargetPosition(): Promise<number> {
        if (this.drag.target === null) {
            this.drag.scroller ??= await this.getScroller().$target.getRect();
            const rect = await this.$getElement().getRect();
            this.drag.target = rect.left - this.drag.scroller.left;
        }

        return this.drag.target;
    }

    /**
     * 드롭이 될 수 있는 드래그대상의 이전/다음 대상을 가져온다.
     *
     * @param direction - left, right
     * @returns 드롭대상
     */
    #getDropTarget(direction: string): Dom | null {
        let $drop: Dom = null;

        if (direction == 'left') {
            Html.all('li', this.getContainer()).some(($item) => {
                if ($item.isEqual(this.$element) == true) {
                    return true;
                }
                $drop = $item;
            });
        } else {
            let found = false;
            Html.all('li:not(.drag)', this.getContainer()).some(($item) => {
                if (found === true) {
                    $drop = $item;
                    return true;
                }
                if ($item.isEqual(this.$element) == true) {
                    found = true;
                }
            });
        }

        return $drop?.getData('movable') == true ? $drop : null;
    }

    /**
     * 드래그 시작 시 호출되는 핸들러
     *
     * @param tracker - 드래그트래커
     */
    async #handleDragStart(tracker: Dragger): Promise<void> {
        this.drag.scroller = await this.getScroller().$target.getRect();
        this.drag.tracker = tracker;
        this.drag.offset = tracker.getStart().x - this.drag.scroller.left - (await this.#getDragTargetPosition());
        this.drag.onScroll = () => this.#handleScroll();
        this.getScroller().addEvent('active', this.drag.onScroll);

        const anchor = '--aui-task-drag-' + Date.now();
        this.drag.$preview = this.$getElement().clone();
        this.drag.$preview.setStyle('position-anchor', anchor);
        this.drag.$preview.addClass('drag');
        this.$getElement().setStyle('anchor-name', anchor);
        this.getContainer().append(this.drag.$preview);
    }

    /**
     * 드래그 중일 때 호출되는 핸들러
     */
    async #handleDrag(): Promise<void> {
        if (this.drag.tracker === null || this.drag.$preview === null) {
            return;
        }

        const x = this.drag.tracker.getPosition().x;
        const move = x - this.drag.scroller.left - (await this.#getDragTargetPosition()) - this.drag.offset;

        const direction = move > 0 ? 'right' : 'left';
        const $drop = this.#getDropTarget(direction);

        /**
         * 이동할 대상이 없다면 프리뷰를 앵커위치로 고정하고, 과정을 중단한다.
         */
        if ($drop === null) {
            this.drag.$preview.setStyle('transform', `translateX(0px)`);
            return;
        }

        this.drag.$preview.setStyle('transform', `translateX(${move}px)`);

        if (x < this.drag.scroller.left + 80) {
            this.getScroller().startScroll('x', -2);
        } else if (x > this.drag.scroller.right - 80) {
            this.getScroller().startScroll('x', 2);
        } else {
            this.getScroller().stopScroll('x');
        }

        const limit = $drop.getOuterWidth() / 2 + 5;
        if (Math.abs(move) <= limit) {
            return;
        }

        if (direction == 'left') {
            $drop.getParent().insertBefore(this.$getElement(), $drop);
        } else {
            $drop.getParent().insertAfter(this.$getElement(), $drop);
        }

        this.drag.target = null;
        this.#handleDrag();
    }

    /**
     * 드래그 중 자동스크롤이 진행중일때 호출되는 핸들러
     */
    #handleScroll(): void {
        this.drag.target = null;
        this.#handleDrag();
    }

    /**
     * 드래그 종료 시 호출되는 핸들러
     */
    #handleDragEnd(): void {
        this.getScroller().stopScroll('x');

        const $preview = this.drag.$preview;
        const $element = this.$getElement();

        this.getScroller().removeEvent('active', this.drag.onScroll);
        this.drag = { scroller: null, target: null, offset: null, tracker: null, onScroll: null, $preview: null };

        if ($preview === null) {
            return;
        }

        $preview.animate({ transform: 'translateX(0)' }, { duration: 200, easing: 'ease-in-out' }, () => {
            $element.setStyle('anchor-name', null);
            $preview.remove();
            this.scrollTo();
            this.onUpdate();
        });
    }
}
